// FeatureStateRow — ONE row of the feature facet, with the owner's four-state control.
//
// Extracted from IntakeTab on 2026-08-27 for a reason beyond tidiness: the Desk is login-gated,
// so an inline control cannot be RENDERED to check, and this repo's standing rule is that UI
// work is verified by looking at it (a gate cannot see a glyph, a colour, or a tooltip). As its
// own component it mounts in src/harness.jsx outside the gate. Same rule as CooperationChips:
// one renderer, reused, never re-implemented per surface.
//
// THE CYCLE LIVES IN src/lib/featureStates.js AND IS TESTED THERE. This file draws; it decides
// nothing. If you are tempted to compute the next state here, don't — that is how two surfaces
// end up disagreeing about what a second click means.
import React from 'react'
import { stateOf, cycle, clear, statesFor } from '../../lib/featureStates.js'

// ⚠️ GLYPH **AND** COLOUR, never colour alone: the three states must be distinguishable to a
// colour-blind agent and in a screenshot pasted into a chat. The glyph carries the meaning; the
// hue only reinforces it. Deliberately off the Desk's status language where it would collide —
// these sit on a 4×4 box, not a chip fill.
const LOOK = {
  yes:     { glyph: '✓', cls: 'border-emerald-500 text-emerald-600 dark:text-emerald-400' },
  no:      { glyph: '✕', cls: 'border-rose-500 text-rose-600 dark:text-rose-400' },
  unknown: { glyph: '?', cls: 'border-amber-500 text-amber-600 dark:text-amber-400' },
}
const UNSET = { glyph: '', cls: 'border-input' }

export default function FeatureStateRow({ feature, filters, onChange, t }) {
  const code = feature.code
  const label = feature.name_ka || feature.name_en || code
  const st = stateOf(filters, code)
  // 0758: only the states this code can actually answer. ✕ is withheld where the corpus holds
  // no recorded absence — offering it would return zero and read as "no such flats".
  const allowed = statesFor(feature)
  const look = st ? LOOK[st] : UNSET
  const tr = t || (k => k)
  return (
    // ⚠️ TAP TARGET, MEASURED 2026-08-28 — the glyph box is 16px, and it is the ONLY way to
    // advance the cycle. On a phone that is barely a third of the 44px guidance, on the control
    // the owner asked for specifically. The row grows to 44px below `md` (the same breakpoint
    // PANEL_IS_MOBILE reads) and the glyph's hit area grows with it via a pseudo-element, so the
    // BOX STAYS 16px and nothing about the panel's look changes — only what your thumb can hit.
    // ⚠️ The vertical expansion is bounded by the row height ON PURPOSE. A bigger inset would
    // overlap the row above and below, and tapping the wrong feature is worse than a small
    // target: a missed tap is obvious, a wrong one silently filters on something you did not ask.
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-0.5 max-md:min-h-11">
      {/* NOT a checkbox, and it must not look like one: a checkbox has two states and would
          read as "off" for three of these four. */}
      <button type="button"
        onClick={() => onChange(cycle(filters, code, allowed))}
        title={`${tr('desk_feature_state_' + (st || 'unset'))} · ${tr('desk_feature_state_next')}` + (allowed.includes('no') ? '' : ` · ${tr('desk_feature_state_no_unavailable')}`)}
        aria-label={`${label} — ${tr('desk_feature_state_' + (st || 'unset'))}`}
        className={`relative h-4 w-4 shrink-0 rounded border text-[11px] leading-none flex items-center justify-center`
          + ` max-md:before:absolute max-md:before:content-[''] max-md:before:-inset-y-3.5 max-md:before:-inset-x-2`
          + ` ${look.cls}`}>
        {look.glyph}
      </button>
      {/* The label is the escape hatch out of the cycle (owner amendment 2026-08-27) — and it is
          a BUTTON only when there is something to clear, or clicking the word would look broken. */}
      {st
        ? <button type="button" className="flex-1 truncate text-left hover:text-foreground cursor-pointer self-stretch"
            title={tr('desk_feature_state_clear')}
            onClick={() => onChange(clear(filters, code))}>{label}</button>
        : <span className="flex-1 truncate">{label}</span>}
    </div>
  )
}
